/**
 * proposal.ts — Blocos de domínio "Proposta" do Studio
 *
 * Registra no catálogo único os blocos que compõem uma proposta (apresentação,
 * itens, totais, condições, rodapé) e os blocos de AÇÃO (aceite/recusa), que
 * possuem comportamento e por isso só existem na saída web.
 *
 * A separação entre bloco de conteúdo e bloco de ação é intencional: o
 * renderizador HTML/PDF ignora a família `action`, enquanto o renderizador web
 * monta um componente React real do sistema.
 *
 * RELEASE: v1.0.0 - 2026-09-09 - Versão inicial (Studio, Fase 2)
 */

import { registerBlocks } from '../blockRegistry';
import type { StudioOutput } from '../types';

/** Blocos de proposta que existem em web e PDF. */
const WEB_PDF: StudioOutput[] = ['web', 'pdf'];

registerBlocks([
  {
    key: 'proposal.hero',
    label: 'Apresentação da proposta',
    family: 'data',
    icon: 'layout-panel-top',
    outputs: WEB_PDF,
    requiresContexts: ['proposal', 'tenant'],
    provides: ['proposal.header', 'proposal.total'],
    props: [
      { key: 'showLogo', label: 'Exibir logotipo', type: 'boolean', defaultValue: true },
      { key: 'showTotal', label: 'Destacar valor total', type: 'boolean', defaultValue: true },
      { key: 'showValidity', label: 'Exibir validade', type: 'boolean', defaultValue: true },
      { key: 'showPdfButton', label: 'Exibir botão de PDF', type: 'boolean', defaultValue: true },
    ],
  },
  {
    key: 'proposal.status',
    label: 'Situação da proposta',
    family: 'data',
    icon: 'badge-check',
    outputs: WEB_PDF,
    requiresContexts: ['proposal'],
    provides: ['proposal.status'],
    props: [],
  },
  {
    key: 'proposal.items',
    label: 'Itens da proposta',
    family: 'data',
    icon: 'list',
    outputs: WEB_PDF,
    requiresContexts: ['proposal'],
    provides: ['proposal.items'],
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text', defaultValue: 'Itens da proposta' },
    ],
  },
  {
    key: 'proposal.totals',
    label: 'Resumo financeiro',
    family: 'data',
    icon: 'calculator',
    outputs: WEB_PDF,
    requiresContexts: ['proposal'],
    provides: ['proposal.total'],
    props: [],
  },
  {
    key: 'proposal.conditions',
    label: 'Condições comerciais',
    family: 'data',
    icon: 'file-text',
    outputs: WEB_PDF,
    requiresContexts: ['proposal'],
    provides: ['proposal.conditions'],
    props: [
      {
        key: 'title',
        label: 'Título do bloco',
        type: 'text',
        defaultValue: 'Condições comerciais',
      },
    ],
  },
  {
    key: 'proposal.footer',
    label: 'Rodapé da proposta',
    family: 'data',
    icon: 'shield-check',
    outputs: WEB_PDF,
    requiresContexts: ['proposal', 'tenant'],
    provides: ['proposal.footer'],
    props: [],
  },

  // ---------------------------------------------------------------------------
  // Blocos de AÇÃO — possuem comportamento (somente web)
  // ---------------------------------------------------------------------------
  {
    key: 'action.proposal.accept',
    label: 'Aceitar proposta',
    family: 'action',
    icon: 'check-circle',
    outputs: ['web'],
    requiresContexts: ['proposal'],
    provides: ['proposal.accept'],
    props: [
      { key: 'label', label: 'Texto do botão', type: 'text', defaultValue: 'Aceitar proposta' },
    ],
  },
  {
    key: 'action.proposal.reject',
    label: 'Recusar proposta',
    family: 'action',
    icon: 'x-circle',
    outputs: ['web'],
    requiresContexts: ['proposal'],
    provides: ['proposal.reject'],
    props: [{ key: 'label', label: 'Texto do botão', type: 'text', defaultValue: 'Recusar' }],
  },
  {
    key: 'action.proposal.decision',
    label: 'Faixa de decisão (aceitar/recusar)',
    family: 'action',
    icon: 'gavel',
    outputs: ['web'],
    requiresContexts: ['proposal'],
    provides: ['proposal.accept', 'proposal.reject'],
    props: [],
  },
]);
